import React, { useEffect, useState, useCallback } from "react";
import { database } from "../firebase";
import { ref, get, onValue } from "firebase/database";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  Box,
  Collapse,
  Fade,
} from "@mui/material";
import { format, formatDistanceToNow } from "date-fns";
import { Remove, HistoryToggleOff } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import FetchDataButton from "./fetchDataButton";
import {
  fetchHeartbeatForWatch,
  subscribeToHeartbeatStatus,
} from "../utils/watchHeartbeat";
import "./bodyComposition.scss";

const BodyComposition = ({}) => {
  const [bodyData, setBodyData] = useState(null);
  const [timestamps, setTimestamps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);

  // NEW: deviceName and online status from /Watches/{watchId}
  const [deviceName, setDeviceName] = useState(null);
  const [heartbeatStatus, setHeartbeatStatus] = useState(null);

  const { watchId } = useParams();

  const WatchesImages = [
    { id: "SM-L305F", url: "/Images/SM-L305F.png", series: "Galaxy Watch7" },
    { id: "SM-L310", url: "/Images/SM-L310.png", series: "Galaxy Watch7" },
    { id: "SM-L315F", url: "/Images/SM-L315F.png", series: "Galaxy Watch7" },
    { id: "SM-L705F", url: "/Images/SM-L705F.png", series: "Galaxy Watch7" },

    { id: "SM-R910", url: "/Images/SM-R910.png", series: "Galaxy Watch6" },
    { id: "SM-R915F", url: "/Images/SM-R915F.png", series: "Galaxy Watch6" },
    { id: "SM-R930", url: "/Images/SM-R930.png", series: "Galaxy Watch6" },
    { id: "SM-R935F", url: "/Images/SM-R935F.png", series: "Galaxy Watch6" },
    { id: "SM-R940", url: "/Images/SM-R940.png", series: "Galaxy Watch6" },
    { id: "SM-R945F", url: "/Images/SM-R945F.png", series: "Galaxy Watch6" },

    // Updated for Galaxy Watch8 series (models you requested for Watch8)
    { id: "SM-L320", url: "/Images/SM-L320.png", series: "Galaxy Watch8" },
    { id: "SM-L325", url: "/Images/SM-L325.png", series: "Galaxy Watch8" },
    { id: "SM-L325U", url: "/Images/SM-L325U.png", series: "Galaxy Watch8" },
    { id: "SM-L330", url: "/Images/SM-L330.png", series: "Galaxy Watch8" },
    { id: "SM-L335", url: "/Images/SM-L335.png", series: "Galaxy Watch8" },
    { id: "SM-L335F", url: "/Images/SM-L335F.png", series: "Galaxy Watch8" },
    { id: "SM-L335U", url: "/Images/SM-L335U.png", series: "Galaxy Watch8" },
    { id: "SM-L500", url: "/Images/SM-L500.png", series: "Galaxy Watch8" },
    { id: "SM-L505F", url: "/Images/SM-L505F.png", series: "Galaxy Watch8" },
  ];

  // fetch deviceName once
  useEffect(() => {
    if (!watchId) return;
    const dRef = ref(database, `/Watches/${watchId}/deviceName`);
    get(dRef)
      .then((snap) => {
        if (snap.exists()) setDeviceName(String(snap.val()));
      })
      .catch((e) => console.error("Error fetching deviceName:", e));
  }, [watchId]);

  const refreshHeartbeatStatus = useCallback(async () => {
    if (!watchId) return null;
    try {
      return await fetchHeartbeatForWatch(watchId);
    } catch (error) {
      console.error("Failed to fetch heartbeat status", error);
      return null;
    }
  }, [watchId]);

  useEffect(() => {
    if (!watchId) return undefined;

    let cancelled = false;

    refreshHeartbeatStatus().then((status) => {
      if (!cancelled && status) {
        setHeartbeatStatus(status);
      }
    });

    const unsubscribe = subscribeToHeartbeatStatus(watchId, (status) => {
      if (!cancelled) {
        setHeartbeatStatus(status);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [refreshHeartbeatStatus, watchId]);

  useEffect(() => {
    if (!watchId) return undefined;

    let cancelled = false;

    const load = async () => {
      const status = await refreshHeartbeatStatus();
      if (!cancelled) {
        setHeartbeatStatus(status);
      }
    };

    const intervalId = setInterval(() => {
      load();
    }, 30_000);

    load();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [refreshHeartbeatStatus, watchId]);

  // derive matched watch meta
  const watchMeta = React.useMemo(
    () => (deviceName ? WatchesImages.find((w) => w.id === deviceName) : null),
    [deviceName]
  );

  const isOnline = heartbeatStatus?.isOnline ?? false;
  const lastOnlineIso = heartbeatStatus?.lastOnline ?? null;
  const lastOnlineDisplay = React.useMemo(() => {
    if (!lastOnlineIso) return null;
    const parsed = new Date(lastOnlineIso);
    if (Number.isNaN(parsed.getTime())) return null;
    try {
      return format(parsed, "PPpp");
    } catch (error) {
      console.error("Failed to format last online timestamp", error);
      return parsed.toLocaleString();
    }
  }, [lastOnlineIso]);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const dataRef = ref(database, `/Health/${watchId}/bodyComposition`);
        onValue(dataRef, (snapshot) => {
          if (snapshot.exists()) {
            const records = snapshot.val();
            const allTimestamps = Object.keys(records);
            const latestTimestamp = allTimestamps.sort(
              (a, b) => Number(b) - Number(a)
            )[0];
            setTimestamps(allTimestamps);
            if (
              !selectedTimestamp ||
              !allTimestamps.includes(selectedTimestamp)
            ) {
              setSelectedTimestamp(latestTimestamp);
            }
          } else {
            console.log("No records available");
          }
        });
      } catch (error) {
        console.error("Error fetching records:", error);
      }
    };

    if (watchId) fetchRecords();
  }, [watchId, selectedTimestamp]);

  useEffect(() => {
    if (selectedTimestamp) {
      const fetchData = async () => {
        try {
          const dataRef = ref(
            database,
            `/Health/${watchId}/bodyComposition/${selectedTimestamp}`
          );
          const snapshot = await get(dataRef);
          if (snapshot.exists()) {
            setBodyData(snapshot.val());
          } else {
            console.log("No data available for selected timestamp");
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };
      fetchData();
    }
  }, [selectedTimestamp, watchId]);

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);
  const handleRecordSelect = (timestamp) => {
    setSelectedTimestamp(timestamp);
    setDialogOpen(false);
  };
  const formatTimestamp = (timestamp) => {
    return format(new Date(parseInt(timestamp)), "MMMM dd, yyyy hh:mm a");
  };
  const formatRelativeTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(Number(timestamp)), {
        addSuffix: true,
      });
    } catch (error) {
      return "--";
    }
  };
  const handleMinimize = () => setMinimized(true);
  const handleExpand = () => setMinimized(false);

  const latestTimestamp = React.useMemo(() => {
    if (!timestamps || timestamps.length === 0) return null;
    return [...timestamps].sort((a, b) => Number(b) - Number(a))[0];
  }, [timestamps]);

  useEffect(() => {
    if (!dialogOpen || !watchId || !timestamps.length) return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      const records = await Promise.all(
        timestamps.map(async (timestamp) => {
          try {
            const dataRef = ref(
              database,
              `/Health/${watchId}/bodyComposition/${timestamp}`
            );
            const snap = await get(dataRef);
            if (snap.exists()) {
              return { timestamp, data: snap.val() };
            }
          } catch (error) {
            console.error("Failed to load record", timestamp, error);
          }
          return null;
        })
      );

      const filtered = records
        .filter(Boolean)
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      if (!cancelled) {
        setHistoryRecords(filtered);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [dialogOpen, timestamps, watchId]);

  const fieldLabels = {
    basalMetabolicRate: {
      label: "Basal Metabolic Rate",
      unit: "kcal",
      icon: "fa-fire",
      color: "#FF9800",
    },
    bodyFatMass: {
      label: "Body Fat Mass",
      unit: "kg",
      icon: "fa-weight",
      color: "#FF5733",
    },
    bodyFatRatio: {
      label: "Body Fat Ratio",
      unit: "%",
      icon: "fa-weight-hanging",
      color: "#FF5733",
    },
    bodyImpedanceDegree: {
      label: "Body Impedance Degree",
      unit: "°",
      icon: "fa-random",
      color: "#9C27B0",
    },
    bodyImpedanceMagnitude: {
      label: "Body Impedance Magnitude",
      unit: "Ω",
      icon: "fa-bolt",
      color: "#03A9F4",
    },
    fatFreeMass: {
      label: "Fat Free Mass",
      unit: "kg",
      icon: "fa-users",
      color: "#007BFF",
    },
    fatFreeRatio: {
      label: "Fat Free Ratio",
      unit: "%",
      icon: "fa-users",
      color: "#007BFF",
    },
    progress: {
      label: "Measurement Progress",
      unit: "",
      icon: "fa-chart-line",
      color: "#607D8B",
    },
    skeletalMuscleMass: {
      label: "Skeletal Muscle Mass",
      unit: "kg",
      icon: "fa-dumbbell",
      color: "#28A745",
    },
    skeletalMuscleRatio: {
      label: "Skeletal Muscle Ratio",
      unit: "%",
      icon: "fa-bone",
      color: "#28A745",
    },
    status: { label: "Status", unit: "", icon: "fa-heart", color: "#FFC107" },
    totalBodyWater: {
      label: "Total Body Water",
      unit: "L",
      icon: "fa-tint",
      color: "#17A2B8",
    },
  };

  return (
    <>
      <Collapse in={!minimized} timeout={300}>
        <section className="body-comp-shell">
          <div className="body-comp-bar">
            <div className="device-pane">
              <div className="device-visual">
                {watchMeta?.url ? (
                  <img
                    src={watchMeta.url}
                    alt={watchMeta.series || deviceName}
                  />
                ) : (
                  <div className="device-placeholder">No preview</div>
                )}
                <p className="device-id-tag device-id-tag--overlay">
                  {watchId || "—"}
                </p>
                <span className="device-series">
                  {watchMeta?.series || "Watch"}
                </span>
              </div>

              <div className="device-meta">
                <div className="device-meta__top">
                  <p className="eyebrow">Body Composition</p>
                  <h3>{`${bodyData?.displayName || "Dashboard overview"} - ${
                    watchId || "Dashboard overview"
                  }`}</h3>

                  <div className="pill-row">
                    <span className="pill pill--solid">
                      {selectedTimestamp
                        ? `Selected · ${formatTimestamp(selectedTimestamp)}`
                        : "Selected · None"}
                    </span>
                    <span className="pill pill--ghost">
                      Last synced ·{" "}
                      {latestTimestamp
                        ? formatTimestamp(latestTimestamp)
                        : "Waiting for data"}
                    </span>
                  </div>
                </div>

                <div className="device-actions">
                  <FetchDataButton
                    label="Request Body Composition"
                    sensorCode={6}
                    sx={{
                      borderRadius: "12px",
                      px: 2.2,
                      py: 0.9,
                      background: "linear-gradient(135deg, #82cbff, #c0e7ff)",
                      boxShadow: "0 8px 22px rgba(130,203,255,0.22)",
                      textTransform: "none",
                      fontWeight: 700,
                      letterSpacing: 0.05,
                      minWidth: 0,
                      color: "#0b1f36",
                      border: "1px solid rgba(133,195,255,0.35)",
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={openDialog}
                    startIcon={<HistoryToggleOff />}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#0b1f36",
                      borderColor: "rgba(133,195,255,0.38)",
                      background:
                        "linear-gradient(135deg, rgba(150,210,255,0.18), rgba(195,230,255,0.14))",
                      px: 2,
                      "&:hover": {
                        borderColor: "rgba(133,195,255,0.52)",
                        background: "rgba(150,210,255,0.2)",
                      },
                    }}
                  >
                    Select record to load
                  </Button>
                </div>

                <div className="device-meta__grid">
                  <div className="meta-chip">
                    <span
                      className={`status-dot ${
                        isOnline ? "online" : "offline"
                      }`}
                    />
                    <div>
                      <p className="label">Status</p>
                      <strong>{isOnline ? "Online" : "Offline"}</strong>
                    </div>
                  </div>
                  <div className="meta-chip">
                    <div className="dot" />
                    <div>
                      <p className="label">Last seen</p>
                      <strong>{lastOnlineDisplay || "—"}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="metrics-pane">
              <div className="metrics-pane__header">
                <div>
                  <p className="eyebrow">Metrics snapshot</p>
                  <p className="muted">
                    {bodyData?.timestamp
                      ? `Selected · ${formatTimestamp(bodyData.timestamp)}`
                      : "Waiting for data"}
                  </p>
                </div>
                {bodyData?.bodyCompositionStatus && (
                  <span className="pill pill--ghost">
                    {bodyData.bodyCompositionStatusDescription ||
                      bodyData.bodyCompositionStatus}
                  </span>
                )}
              </div>

              {!bodyData ? (
                <div className="empty-state empty-state--inline">
                  <p>
                    No body composition data yet. Request a scan to populate
                    this dashboard header.
                  </p>
                </div>
              ) : (
                (() => {
                  const entries = Object.entries(bodyData).filter(
                    ([key]) =>
                      ![
                        "displayName",
                        "timestamp",
                        "bodyCompositionStatus",
                        "bodyCompositionStatusDescription",
                        "sensorType",
                      ].includes(key)
                  );

                  return (
                    <div className="metric-lineup">
                      {entries.map(([key, value]) => {
                        const field = fieldLabels[key];
                        const displayValue =
                          typeof value === "number"
                            ? value.toFixed(2)
                            : String(value);

                        return (
                          <div className="metric-line" key={key}>
                            <div
                              className="metric-line__icon"
                              style={{ color: field?.color }}
                            >
                              {field?.icon && (
                                <span className={`fa ${field.icon}`} />
                              )}
                            </div>
                            <div className="metric-line__body">
                              <p className="label">{field?.label || key}</p>
                              <strong>
                                {displayValue}
                                {field?.unit && (
                                  <span className="unit">{` ${field.unit}`}</span>
                                )}
                              </strong>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
          </div>

          <div className="body-comp-footer">
            <Button
              onClick={handleMinimize}
              startIcon={<Remove />}
              className="body-comp-minimize"
              variant="text"
              size="small"
            >
              Minimize
            </Button>
          </div>

          <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm">
            <DialogTitle className="history-dialog__title">
              Select a record
            </DialogTitle>
            <DialogContent className="history-dialog__content">
              <Box className="history-dialog__list">
                {historyRecords.length === 0 && (
                  <div className="history-dialog__empty">
                    <p>No past records available yet.</p>
                  </div>
                )}
                {historyRecords.map(({ timestamp, data }) => {
                  const summaryMetrics = [
                    { key: "fatFreeMass", label: "Fat-free mass" },
                    { key: "bodyFatMass", label: "Body fat" },
                    { key: "totalBodyWater", label: "Total body water" },
                  ];

                  return (
                    <ListItemButton
                      key={timestamp}
                      onClick={() => handleRecordSelect(timestamp)}
                      className="history-dialog__item"
                    >
                      <div className="history-dialog__item-top">
                        <div>
                          <p className="label">{formatTimestamp(timestamp)}</p>
                          <p className="sub">{formatRelativeTime(timestamp)}</p>
                        </div>
                        <div className="pill-row">
                          {latestTimestamp === timestamp && (
                            <span className="pill pill--solid">Latest</span>
                          )}
                          {data?.bodyCompositionStatusDescription && (
                            <span className="pill pill--ghost">
                              {data.bodyCompositionStatusDescription}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="history-dialog__metrics">
                        {summaryMetrics.map(({ key, label }) => {
                          const val = data?.[key];
                          if (val === undefined || val === null) return null;
                          return (
                            <div className="metric-chip" key={key}>
                              <span className="metric-chip__label">
                                {label}
                              </span>
                              <strong>
                                {typeof val === "number" ? val.toFixed(2) : val}
                              </strong>
                            </div>
                          );
                        })}
                      </div>
                    </ListItemButton>
                  );
                })}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
              <Button
                onClick={closeDialog}
                variant="outlined"
                sx={{ textTransform: "none" }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </section>
      </Collapse>

      <Fade in={minimized} timeout={300}>
        <div className="body-comp-floating">
          <Button
            variant="contained"
            color="primary"
            onClick={handleExpand}
            size="small"
            sx={{
              minWidth: 0,
              fontSize: "12px",
              px: 1.5,
              py: 0.75,
              borderRadius: "12px",
              textTransform: "none",
            }}
          >
            Show Body Composition
          </Button>
        </div>
      </Fade>
    </>
  );
};

export default BodyComposition;
